import React, { FC, useEffect, useState } from 'react';
import {
  Backdrop,
  Grid,
  Container,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faRotateRight,
  faSquareMinus,
  faBullhorn,
  faCircleUser,
  faFileMagnifyingGlass,
  faAddressBook,
  faFileImage,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  BoxV2 as Box,
  Flex,
  Table,
  FilterType,
  TableFilter,
  FilterConfig,
  useTable,
  useFilters,
} from 'portal-commons';

import { Loader, FilterToggleButton } from '@/shared_elements';
import {
  CampaignListingRow,
  BrandDetailsBasic,
  BrandCspDetails,
  BrandAddressDetails,
  VettingDetailsListingRow,
  BrandDetailsSection,
  BrandActionModal,
  BrandContactDetails,
  BrandAssets,
} from '../../components';
import Spinner from '../../../Campaigns/components/Spinner';
import { enumController } from '@/apis/core';
import {
  fetchCspSuggestions,
  getBrandDetail,
  getVettingRecords,
  getCampaignsByBrand,
  getSuspensionRules,
  getAllCampaignUidsByBrand,
  updateCampaignsStatus,
} from '../../apis';
import { MNO_STATUS } from '@/constants';
import { toastFlashMessage } from '@/utils';
import '../../assets/brand-details-module.scss';
import { AdditionalFilters } from '@/utils/types';
import { Brand, VettingRecord } from '../../types';
import { CampaignsData } from '../../../Campaigns/types';

const FILTER_CONFIG: Record<string, FilterConfig> = {
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
    placeholder: 'Use-Case',
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA Name',
    suggestions: [],
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
    placeholder: 'TCR Status',
  },
  mnoStatus: {
    type: FilterType.Dropdown,
    options: MNO_STATUS,
    label: 'MNO Status',
    placeholder: 'MNO Status',
    multiple: true,
    width: 250,
  },
};

const headRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'usecase', label: 'USE-CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'status', label: 'TCR STATUS', sortable: false },
  { id: 'operationStatus', label: 'MNO STATUS', sortable: false },
];

const VettingDetailHeadRows = [
  { id: 'type', label: 'VETTING TYPE', sortable: false },
  { id: 'partner', label: 'VETTING PARTNER', sortable: false },
  { id: 'date', label: 'VETTED ON', sortable: false },
  { id: 'outcome', label: 'OUTCOME', sortable: false },
  { id: 'status', label: 'VETTING STATUS', sortable: false },
];

const fetchCspNameSuggestions = async (query = {}): Promise<string[]> => {
  const suggestions = await fetchCspSuggestions(query);
  return suggestions.map((suggestion) => suggestion.cspName);
};

interface LocationState {
  brandInfo?: Brand;
  goBackPage?: string;
}

const DEFAULT_BRANDS_LIST = '/brands?status=ACTIVE';
const FILTER_LOADERS = {
  suggestion: {
    cspName: {
      minLength: 2,
      load: (value: string) => fetchCspNameSuggestions({ prefix: value }),
    },
  },
};

const BrandDetail: FC<{ brandUid: string }> = ({ brandUid }) => {
  const campaignFilter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });
  const location = useLocation();
  const history = useHistory();
  const [loader, setLoader] = useState(true);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [brandAction, setBrandAction] = useState('');
  const [brandInfo, setBrandInfo] = useState<Brand>();
  const [brandSuspended, setBrandSuspended] = useState<boolean | null>(null);
  const [goBackPage, setGoBackPage] = useState<string>(DEFAULT_BRANDS_LIST);
  const locationState = location.state as LocationState | undefined;

  const vettingRecordTable = useTable<VettingRecord>(
    async () => {
      const records = (await getVettingRecords(brandUid)) || [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.min(records.length, 10),
      };
    },
    {
      deps: [brandUid],
      inMemory: true,
    }
  );

  const fetchCampaignsByBrand = async (query: any) => {
    const updatedQuery = { ...query };
    const isStatusExpired = query?.status === 'EXPIRED';
    if (isStatusExpired && !query?.mnoStatus) {
      updatedQuery.mnoStatus = 'REJECTED,REGISTERED,SUSPENDED';
    }
    if (
      updatedQuery?.status &&
      (updatedQuery.status as string).split(',').length > 1
    ) {
      delete updatedQuery.status;
    }
    const campaignData = await getCampaignsByBrand(brandUid, updatedQuery);
    return {
      data: campaignData?.records || [],
      total: campaignData?.totalRecords || 0,
      rowsPerPage: campaignData?.recordsPerPage || 10,
      page: campaignData?.page || 1,
    };
  };

  const gatherCampaignTableData = () => ({
    data: campaignTable.tableData.slice(),
    total: campaignTable.total,
    page: campaignTable.filter.page,
    rowsPerPage: campaignTable.pagination.rowsPerPage,
  });

  const campaignTable = useTable<CampaignsData>(
    async (
      tableFilter: any
    ): Promise<{
      data: CampaignsData[];
      total: number;
      page: number;
      rowsPerPage: number;
    }> => {
      const query = {
        ...tableFilter,
        ...campaignFilter.appliedFilters,
        ...campaignFilter.additionalFilters,
        channelId: '10DLC',
      };

      if (
        query.status === 'EXPIRED' &&
        (query.mnoStatus as string)?.split(',').includes('REVIEW')
      ) {
        return gatherCampaignTableData();
      }

      return fetchCampaignsByBrand(query);
    },
    {
      deps: [
        brandUid,
        campaignFilter.appliedFilters,
        campaignFilter.additionalFilters,
      ],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const fetchFilterOptions = async () => {
    const { setOptions } = campaignFilter;
    const usecaseTypes = await enumController.getUsecaseTypes({
      channelId: '10DLC',
    });

    setOptions(
      'usecase',
      usecaseTypes.map((item) => ({
        label: item.displayName,
        value: item.id,
      }))
    );
  };

  const updateMnoStatusReviewOptionDisabled = (disabled = false) => {
    const { setOptions } = campaignFilter;
    setOptions(
      'mnoStatus',
      MNO_STATUS.map((item) =>
        item.value === 'REVIEW' ? { ...item, disabled } : item
      )
    );
  };

  const processFilterSpecialCase = (
    values: AdditionalFilters | Record<string, string>,
    callback: (filters: Record<string, any>) => void
  ): void => {
    const isStatusExpired = values?.status === 'EXPIRED';
    updateMnoStatusReviewOptionDisabled(isStatusExpired);

    if (isStatusExpired) {
      const hasReviewInMnoStatus = (values?.mnoStatus as string)?.includes(
        'REVIEW'
      );
      if (hasReviewInMnoStatus) {
        const newMnoStatus = (values.mnoStatus as string)
          .split(',')
          .filter((item) => item !== 'REVIEW')
          .join(',');
        if (newMnoStatus) {
          callback({ ...values, mnoStatus: newMnoStatus });
        } else {
          callback({ ...values, mnoStatus: undefined });
        }
      }
    }
  };

  const fetchBrandDetail = async (uid: string) => {
    const data = await getBrandDetail(uid);
    if (data) {
      setBrandInfo(data);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (locationState?.goBackPage) {
      setGoBackPage(locationState.goBackPage);
    }
    if (locationState?.brandInfo) {
      setBrandInfo(locationState.brandInfo);
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    if (!locationState?.brandInfo) {
      fetchBrandDetail(brandUid);
    }
  }, [brandUid, locationState?.brandInfo]);

  useEffect(() => {
    fetchFilterOptions();
    checkSuspensionStatus();
  }, []);

  useEffect(() => {
    processFilterSpecialCase(
      campaignFilter.appliedFilters,
      campaignFilter.handleApply
    );
  }, [
    campaignFilter.appliedFilters.status,
    campaignFilter.appliedFilters.mnoStatus,
  ]);

  useEffect(() => {
    processFilterSpecialCase(
      campaignFilter.candidateFilters,
      campaignFilter.handleEdit
    );
  }, [
    campaignFilter.candidateFilters.status,
    campaignFilter.candidateFilters.mnoStatus,
  ]);

  const checkSuspensionStatus = async () => {
    const rules = await getSuspensionRules({ brandUid, exactMatch: true });
    if (rules && rules.records.length > 0) {
      const found = rules.records.some(
        (rule) => rule.brandUid === brandUid && rule.status === 'ACTIVE'
      );
      setBrandSuspended(found);
    } else {
      setBrandSuspended(false);
    }
  };

  const handleGoBack = () => {
    history.push(`${goBackPage}`);
  };

  const bulkUpdateCampaignsStatus = async (
    uids: string[],
    status: 'SUSPENDED' | 'REGISTERED',
    explanation: string,
    suspensionCategory?: string,
    totalUids?: number
  ): Promise<boolean> => {
    const total = totalUids || uids.length;
    const batchUids = uids.splice(0, 10);
    const inProcess = total - uids.length;
    const actionText = status === 'SUSPENDED' ? 'Suspending' : 'Lifting';
    setModalMessage(`${actionText} ${inProcess}/${total} campaigns...`);

    await updateCampaignsStatus(
      batchUids,
      status,
      explanation,
      suspensionCategory
    );

    if (uids.length > 0) {
      return bulkUpdateCampaignsStatus(
        uids,
        status,
        explanation,
        suspensionCategory,
        total
      );
    }
    return true;
  };

  const suspendAllCampaigns = async (
    explanation: string,
    suspensionCategory: string
  ) => {
    setModalOpen(true);
    setModalMessage('Suspending all campaigns...');
    const campaignUids = await getAllCampaignUidsByBrand(
      brandUid,
      'ACTIVE',
      'REGISTERED'
    );

    await bulkUpdateCampaignsStatus(
      campaignUids,
      'SUSPENDED',
      explanation,
      suspensionCategory
    );

    setModalOpen(false);
    setModalMessage('');
    campaignTable.reload();
    toastFlashMessage('Successfully updated campaign status', 'success');
  };

  const liftAllSuspensions = async (explanation: string) => {
    setModalOpen(true);
    setModalMessage('Lift all suspensions...');
    const campaignUids = await getAllCampaignUidsByBrand(
      brandUid,
      'ACTIVE',
      'SUSPENDED'
    );

    await bulkUpdateCampaignsStatus(campaignUids, 'REGISTERED', explanation);

    setModalOpen(false);
    setModalMessage('');
    campaignTable.reload();
    toastFlashMessage('Successfully updated campaign status', 'success');
  };

  const handleCampaignAction =
    (action: 'suspend' | 'lift') => (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (brandInfo?.status === 'DELETED') {
        const message =
          action === 'suspend'
            ? 'Campaigns are deactivated and cannot be suspended.'
            : 'Campaigns are deactivated and cannot have suspensions lifted.';
        toastFlashMessage(message, 'error');
      } else {
        setBrandAction(action);
      }
    };

  const goToBrandSuspension = () => {
    const brandStatus = brandInfo?.status || 'ACTIVE';
    if (brandStatus === 'DELETED') {
      toastFlashMessage(
        'Brand is deleted and cannot be suspended or have suspension lifted.',
        'error'
      );
      return;
    }
    history.push({
      pathname: `/brand-suspension/${brandUid}`,
      state: { brandSuspended },
    });
  };

  return (
    <section className="brand-details-section page-content">
      {loader || !brandInfo ? (
        <Loader />
      ) : (
        <Container maxWidth={false} className="brand-details-container">
          <Grid container className="top-blk" style={{ height: '40px' }}>
            <Grid item xs={6} className="back-btn">
              <a onClick={handleGoBack} className="paragraph">
                <Flex sx={{ alignItems: 'center' }}>
                  <FontAwesomeIcon
                    icon={faAngleLeft}
                    size="lg"
                    style={{ marginRight: '12px' }}
                  />
                  {goBackPage.startsWith('/brands')
                    ? 'back to all brands'
                    : 'back'}
                </Flex>
              </a>
            </Grid>
            <Grid item xs={6} className="button-wrapper flex-centered">
              {brandSuspended === null ? (
                <CircularProgress
                  size={40}
                  style={{ color: '#00698F', marginRight: '50px' }}
                />
              ) : brandSuspended ? (
                <React.Fragment>
                  <Button
                    color="secondary"
                    data-testid="gotoLiftBrandSuspensionButton"
                    onClick={goToBrandSuspension}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                    <span>Lift Brand Suspension</span>
                  </Button>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Button
                    data-testid="gotoBrandSuspensionButton"
                    onClick={goToBrandSuspension}
                  >
                    <Box>
                      <FontAwesomeIcon icon={faSquareMinus} size="lg" />
                    </Box>
                    <span>Suspend Brand</span>
                  </Button>
                </React.Fragment>
              )}
            </Grid>
          </Grid>
          <Grid container className="brand-details basic">
            <Grid item xs={12}>
              <div className="title" data-testid="brandDetailDisplayName">
                <h3 className="heading1">
                  Brand DBA Name:{' '}
                  {brandInfo.entityType.toLowerCase() === 'sole_proprietor'
                    ? 'Redacted due to PII'
                    : brandInfo.displayName
                    ? brandInfo.displayName
                    : 'N/A'}
                </h3>
                {brandInfo.entityType.toLowerCase() !== 'sole_proprietor' && (
                  <p className="heading2">
                    Brand Legal Name:{' '}
                    {brandInfo.companyName ? brandInfo.companyName : 'N/A'}
                  </p>
                )}
              </div>
            </Grid>
            <Grid item xs={12}>
              <BrandDetailsBasic
                brandInfo={brandInfo}
                brandSuspended={brandSuspended}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <div className="contact details">
                <div className="details-heading">
                  <Flex sx={{ alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={faCircleUser}
                      style={{ fontSize: '18px', marginRight: 8 }}
                    />
                    <h3>CSP Details</h3>
                  </Flex>
                </div>
                <BrandCspDetails cspInfo={brandInfo.csp} />
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className="contact details">
                <div className="details-heading">
                  <Flex sx={{ alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={faAddressBook}
                      style={{ fontSize: '18px', marginRight: 8 }}
                    />
                    <h3>Brand Details</h3>
                  </Flex>
                </div>
                <BrandDetailsSection brand={brandInfo as Brand} />
                <div className="details-heading" style={{ marginTop: '30px' }}>
                  <Flex sx={{ alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={faCircleUser}
                      style={{ fontSize: '18px', marginRight: 8 }}
                    />
                    <h3>Brand Address Details</h3>
                  </Flex>
                </div>
                <BrandAddressDetails
                  street={brandInfo.street}
                  city={brandInfo.city}
                  state={brandInfo.state}
                  postalCode={brandInfo.postalCode}
                  country={brandInfo.country}
                />
                {brandInfo?.entityType === 'PUBLIC_PROFIT' && (
                  <>
                    <Box className="details-heading" mt="xxl">
                      <Flex sx={{ alignItems: 'center' }}>
                        <Box mr="xs">
                          <FontAwesomeIcon icon={faAddressBook} size="xl" />
                        </Box>
                        <h3>Business Contact Details</h3>
                      </Flex>
                    </Box>
                    <BrandContactDetails data={brandInfo} />
                  </>
                )}
              </div>
            </Grid>
            {brandInfo.entityType !== 'SOLE_PROPRIETOR' && (
              <Grid item xs={12}>
                <div className="details">
                  <div className="details-heading border-none">
                    <Flex sx={{ alignItems: 'center' }}>
                      <FontAwesomeIcon
                        icon={faFileImage}
                        style={{ fontSize: '18px', marginRight: 8 }}
                      />
                      <h3>Brand Assets</h3>
                    </Flex>
                  </div>
                  <BrandAssets brand={brandInfo} />
                </div>
              </Grid>
            )}
            <Grid item xs={12}>
              <div className="vetting details">
                <div className="details-heading">
                  <Flex sx={{ alignItems: 'center' }}>
                    <Box mr="xs">
                      <FontAwesomeIcon icon={faFileMagnifyingGlass} size="xl" />
                    </Box>
                    <h3>Vetting Details</h3>
                  </Flex>
                </div>
                <Table
                  data-testid="vettingListingTable"
                  className="vetting-listing-table"
                  loading={false}
                  headRows={VettingDetailHeadRows}
                  emptyState="no vettings to view"
                  disableHover={true}
                  tableData={vettingRecordTable.tableData.map(
                    (record, index) => (
                      <VettingDetailsListingRow key={index} record={record} />
                    )
                  )}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className="campaigns-listing details">
                <Flex>
                  <div className="w-100">
                    <Flex sx={{ alignItems: 'center' }}>
                      <Box mr="xs">
                        <FontAwesomeIcon icon={faBullhorn} size="xl" />
                      </Box>
                      <h3>{`${campaignTable.total} Campaigns`}</h3>
                    </Flex>
                  </div>
                </Flex>
                <Flex
                  sx={{
                    flexDirection: 'column',
                    mt: 'xs',
                    mb: 's',
                  }}
                >
                  <Flex sx={{ justifyContent: 'space-between' }}>
                    <FilterToggleButton
                      visible={isFilterVisible}
                      onClick={() => setIsFilterVisible(!isFilterVisible)}
                      size="small"
                    />
                    <Flex sx={{ gap: 's' }}>
                      <Button
                        data-testid="suspendAllCampaignButton"
                        portal="mno"
                        shape="rounded"
                        variant="standard"
                        color="primary"
                        size="small"
                        disabled={!campaignTable.total}
                        onClick={handleCampaignAction('suspend')}
                      >
                        <Box>
                          <FontAwesomeIcon icon={faSquareMinus} size="lg" />
                        </Box>
                        <span>Suspend All Campaigns</span>
                      </Button>
                      <Button
                        data-testid="liftAllSuspensionButton"
                        portal="mno"
                        shape="rounded"
                        variant="standard"
                        color="secondary"
                        size="small"
                        disabled={!campaignTable.total}
                        onClick={handleCampaignAction('lift')}
                      >
                        <FontAwesomeIcon icon={faRotateRight} color="#FFFFFF" />
                        <span>Lift All Campaign Suspensions</span>
                      </Button>
                    </Flex>
                  </Flex>
                  {isFilterVisible && (
                    <TableFilter
                      portal="mno"
                      configs={campaignFilter.configs}
                      candidateValues={campaignFilter.candidateFilters}
                      appliedValues={campaignFilter.appliedFilters}
                      onCandidateValuesChange={campaignFilter.handleEdit}
                      onAppliedValuesChange={campaignFilter.handleApply}
                      className="filters"
                      data-testid="brandDetailCampaigns"
                    />
                  )}
                </Flex>
                <Table
                  data-testid="campaignListingTable"
                  loading={campaignTable.loading}
                  emptyState="no campaigns to view"
                  handleChangePage={campaignTable.handleChangePage}
                  createSortHandler={campaignTable.handleSorting}
                  filter={campaignTable.filter}
                  headRows={headRows}
                  tableData={campaignTable.tableData.map((data) => (
                    <CampaignListingRow
                      key={data?.uid as string}
                      campaign={data as unknown as CampaignsData}
                    />
                  ))}
                  pagination={campaignTable.pagination}
                />
              </div>
            </Grid>
          </Grid>
          <Backdrop
            style={{
              background: 'rgba(14, 31, 43, 0.75)',
              zIndex: 1005,
            }}
            open={modalOpen}
          >
            <Container className="modal">
              <Spinner />
              <Typography
                variant="body1"
                align="center"
                style={{
                  width: '100%',
                  marginTop: 40,
                  color: '#49535D',
                  wordBreak: 'break-all',
                }}
              >
                {modalMessage}
              </Typography>
            </Container>
          </Backdrop>
        </Container>
      )}
      <BrandActionModal
        open={!!brandAction}
        type={brandAction}
        cspUid={brandInfo?.cspUid ?? ''}
        brandName={
          brandInfo?.displayName
            ? brandInfo.displayName
            : brandInfo?.entityType === 'SOLE_PROPRIETOR'
            ? 'Redacted due to PII'
            : 'N/A'
        }
        onCancel={() => setBrandAction('')}
        onSubmit={(explanation, suspensionCategory) => {
          if (brandAction === 'suspend') {
            suspendAllCampaigns(explanation, suspensionCategory);
          } else {
            liftAllSuspensions(explanation);
          }
          setBrandAction('');
        }}
      />
    </section>
  );
};

export default BrandDetail;
